import posthog from "posthog-js"
import { env } from "@/lib/env"
import type { Me } from "@/features/auth/hooks/useCurrentUser"

export type AnalyticsEvent =
  | "search_submitted"
  // Nira's funnel: a document in, a reading out, and the way to Pro.
  | "notice_dropped"
  | "reading_started"
  | "reading_viewed"
  | "upgrade_clicked"
  | "checkout_result"
  | "paywall_shown"
  | "firms_contact_clicked"
  | "department_card_clicked"
  | "facts_checked"
  | "document_attached"
  | "reading_rerun"
  // The research router: a notice or an order sent as a question, and the reader following it to its door.
  | "research_routed"
  | "research_routed_go"
  // The public home: a question or a notice handed to the app, and the hard samples tried.
  | "site_question_sent"
  | "site_notice_attached"
  | "site_sample_picked"
  | "reading_question_asked"
  | "history_opened"
  | "history_item_deleted"
  | "sim_door_selected"
  | "sim_pricing_shown"
  | "sim_session_started"
  | "sim_turn_submitted"
  | "sim_turn_completed"
  | "sim_turn_failed"
  | "draft_exported"
  | "session_id_copied"
  // The account pages: Profile and Subscription, subscribe and cancel.
  | "profile_opened"
  | "subscription_opened"
  | "workspaces_opened"
  | "subscribe_clicked"
  | "addon_clicked"
  | "subscription_cancel_confirmed"
  | "manage_account_opened"
  // A drafting question answered with the simulator door (OPA policy), and the door taken.
  | "sim_suggestion_clicked"
  // Auth: once per tab session after sign-in, and on sign-out. (`signed_up` is the backend's: /me carries no created date yet.)
  | "signed_in"
  | "signed_out"
  // Nira answers: finished, failed, a source opened, and what the reader did with the answer.
  | "answer_completed"
  | "answer_failed"
  | "citation_opened"
  | "answer_shared"
  | "answer_copied"
  | "answer_exported"
  | "result_voted"
  // Reading behaviour on an answer: one summary per view, the engaged step of the funnel, a stream left unfinished.
  | "answer_view_summary"
  | "answer_engaged"
  | "answer_stream_aborted"
  // Matters: made, opened, sessions moved in or out, files added or removed.
  | "matter_created"
  | "matter_opened"
  | "session_moved_to_matter"
  | "matter_file_added"
  | "matter_file_deleted"
  | "matter_file_linked"
  // The simulators (OIO, OIA): documents in, the mode, each ask shown and answered, the draft.
  | "sim_document_uploaded"
  | "sim_upload_failed"
  | "sim_mode_selected"
  | "sim_ask_shown"
  | "sim_ask_answered"
  | "sim_draft_completed"
  | "sim_draft_opened"
  | "sim_stopped_early"
  // The legacy OIA flow (features/oia).
  | "oia_draft_started"
  | "oia_draft_revised"
  // A real allowance used up (a 402), as against paywall_shown which also covers a plain upgrade ask.
  | "quota_reached"
  // A thumb on an answer or a draft (never the reasons or the note themselves).
  | "response_feedback"

export type AnalyticsApp = "nira" | "oio_sim" | "oia_sim"
type Props = Record<string, string | number | boolean | null>

export type SimName = "oio" | "oia"
export type UploadVia = "picker" | "drop" | "paste"
export type SimAskKind = "question" | "document" | "checkpoint" | "stop"

/** Typed props for the events added with the product-analytics pass. Counts, ids, codes and booleans only; never text. */
export interface EventProps {
  signed_in: { method?: string }
  signed_out: Record<string, never>
  answer_completed: { mode: string; duration_ms: number; sources_count: number; status: "ok" | "partial" }
  answer_failed: { code: string }
  citation_opened: { kind: "case" | "law" | "circular" }
  answer_shared: { channel: "whatsapp" | "email" | "copy_link" | "pdf" | "native" }
  answer_copied: { format: "text" | "link" | "citation" }
  answer_exported: { format: "print" }
  result_voted: { verdict: "confirmed" | "contradicted" }
  answer_view_summary: {
    mode: string | null
    answer_words_bucket: "<200" | "200-500" | "500-1000" | "1000+"
    dwell_ms: number
    reading_effort: number | null
    coverage: number
    reread_ratio: number
    load_score_base: number
    aborted: boolean
    abort_phase: "streaming" | "reading" | null
    exit: "followup" | "navigate" | "close" | "idle" | null
  }
  answer_engaged: { mode: string | null; coverage: number; dwell_ms: number }
  answer_stream_aborted: { phase: "streaming"; mode: string | null }
  matter_created: { from: "picker" | "empty_state" | "notice" | "list" }
  matter_opened: Record<string, never>
  session_moved_to_matter: { type: "nira" | "oio" | "oia"; direction: "in" | "out" }
  matter_file_added: { count: number; via: UploadVia }
  matter_file_deleted: Record<string, never>
  matter_file_linked: { linked: boolean }
  sim_document_uploaded: { sim: SimName; count: number; via: UploadVia; pdf: number; docx: number; image: number }
  sim_upload_failed: { sim: SimName; reason: "too_large" | "unsupported" | "server" }
  sim_mode_selected: { sim: SimName; quick_rough_draft: boolean }
  sim_ask_shown: { sim: SimName; kind: SimAskKind; stage: string }
  sim_ask_answered: {
    sim: SimName
    kind: SimAskKind
    answer: "option" | "text" | "upload" | "not_available" | "already_in" | "resume" | "stop_here"
  }
  sim_draft_completed: { sim: SimName; sections: number; duration_ms: number }
  sim_draft_opened: { sim: SimName }
  sim_stopped_early: { sim: SimName; written: number; total: number }
  oia_draft_started: { status: string }
  oia_draft_revised: { status: string }
  quota_reached: { metric: string; feature?: string | null }
  paywall_shown: { metric: string; reason: string; feature?: string | null; offer?: string | null; trigger?: number | null }
  response_feedback: { app: AnalyticsApp; vote: "up" | "down" | "cleared"; reasons_count: number; has_comment: boolean }
}

type PropsFor<E extends AnalyticsEvent> = E extends keyof EventProps ? EventProps[E] : Props

/** CSS class on the answer body: session replay replaces anything with it by a blank block. */
export const REPLAY_BLOCK_CLASS = "ph-no-capture"

/** Privacy-restrictive PostHog options (no autocapture, heatmaps, dead clicks; all text/attributes/inputs masked; 10% of sessions recorded with the answer body blocked). */
export const POSTHOG_PRIVACY_OPTIONS = {
  defaults: "2026-05-30",
  disable_session_recording: false,
  session_recording: {
    sampleRate: 0.1,
    maskAllInputs: true,
    blockClass: REPLAY_BLOCK_CLASS,
    blockSelector: `.${REPLAY_BLOCK_CLASS}, [data-answer-body]`,
  },
  rageclick: true,
  autocapture: false,
  capture_dead_clicks: false,
  enable_heatmaps: false,
  mask_all_text: true,
  mask_all_element_attributes: true,
  // Anonymous visitors (login page) create no person profile until identifyUser runs.
  person_profiles: "identified_only",
  // SPA route changes count as pageviews (funnels). The URL is scrubbed to its path by scrubUrls (main.tsx before_send).
  capture_pageview: "history_change",
} as const

const URL_KEYS = ["$current_url", "$referrer", "$initial_current_url", "$initial_referrer"]
const stripUrl = (v: unknown): unknown => {
  if (typeof v !== "string") return v
  try {
    const u = new URL(v)
    return `${u.origin}${u.pathname}`
  } catch {
    return v
  }
}

/** PostHog `before_send`: keep only origin + path on every URL property (query strings and hashes can carry prefilled text). */
export function scrubUrls<T extends { properties?: Record<string, unknown> } | null>(event: T): T {
  if (!event?.properties) return event
  for (const k of URL_KEYS) if (k in event.properties) event.properties[k] = stripUrl(event.properties[k])
  return event
}

/** PostHog is only initialised (by PostHogProvider) when a project token is configured. */
const ready = (): boolean => Boolean(posthog.__loaded)

function registerContext(): void {
  posthog.register({ environment: env.VITE_APP_ENV, app_version: env.VITE_APP_VERSION })
}

export function initAnalyticsContext(): void {
  if (!ready()) return
  registerContext()
}

/** Identify with OUR user id (never the Clerk id) and group by organization. Never sends email or name: the privacy policy promises it. */
export function identifyUser(me: Me): void {
  if (!ready()) return
  registerContext() // reset() drops super properties
  const set: Record<string, string> = { role: me.role, plan: me.entitlements?.subscribed ? "pro" : "free" }
  const once: Record<string, string> = me.created_at ? { signed_up_at: me.created_at } : {}
  posthog.identify(me.user_id, set, once)
  posthog.group("organization", me.organization_id)
}

const SIGNED_IN_KEY = "nira.analytics.signed_in"

/** `signed_in`, once per tab session (a reload of the same session does not repeat it). Call after identifyUser. */
export function trackSignedIn(userId: string): void {
  try {
    if (sessionStorage.getItem(SIGNED_IN_KEY) === userId) return
    sessionStorage.setItem(SIGNED_IN_KEY, userId)
  } catch {
    /* storage blocked: better one event per load than none */
  }
  track("signed_in")
}

/** `signed_out`, before the reset so it still belongs to the person. */
export function trackSignedOut(): void {
  track("signed_out")
  try {
    sessionStorage.removeItem(SIGNED_IN_KEY)
  } catch {
    /* ignore */
  }
}

export function resetUser(): void {
  if (!ready()) return
  posthog.reset()
  registerContext()
}

export function enterSession(app: AnalyticsApp, sessionId: string): void {
  if (!ready()) return
  posthog.register({ session_id: sessionId, app })
}

export function leaveSession(): void {
  if (!ready()) return
  posthog.unregister("session_id")
  posthog.unregister("app")
}

/** Never pass prompt, document or message text in props. */
export function track<E extends AnalyticsEvent>(event: E, props?: PropsFor<E>): void {
  if (!ready()) return
  posthog.capture(event, props)
}
